import React, { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Pencil, X, Navigation, Globe, Search } from 'lucide-react';
import { PRESET_COLORS } from './GeofencingUtils';
import { PlaceIconPicker } from './PlaceIconPicker';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { NOMINATIM_BASE } from '../../utils/nominatim';

const ColorPickerField = ({ value, onChange }) => (
  <div className="space-y-1.5" data-testid="color-picker-field">
    <Label className="text-xs">Color</Label>
    <div className="flex flex-wrap items-center gap-1.5">
      {PRESET_COLORS.map((preset) => (
        <button
          key={preset.value}
          type="button"
          title={preset.label}
          onClick={() => onChange(preset.value)}
          className={`w-6 h-6 rounded-full shrink-0 transition-all ${value === preset.value ? 'border-[3px] border-blue-500 scale-110' : 'border-2 border-transparent hover:border-gray-400'}`}
          style={{ backgroundColor: preset.value }}
          data-testid={`preset-color-${preset.value.replace('#', '')}`}
        />
      ))}
      <input
        type="color"
        value={value || '#2563eb'}
        onChange={(event) => onChange(event.target.value)}
        className="w-6 h-6 rounded cursor-pointer border-2 border-gray-300 p-0"
        title="Custom color"
        data-testid="custom-color-input"
      />
      <span className="text-[10px] text-muted-foreground">Custom</span>
    </div>
  </div>
);

const formatNominatimAddress = (data) => {
  const a = data.address;
  if (!a) return data.display_name || '';
  const parts = [];
  const street = [a.house_number, a.road].filter(Boolean).join(' ');
  if (street) parts.push(street);
  const suburb = a.suburb || a.village || a.town || a.hamlet;
  if (suburb) parts.push(suburb);
  if (a.state) parts.push(a.state);
  if (a.postcode) parts.push(a.postcode);
  if (a.country) parts.push(a.country);
  return parts.length ? parts.join(', ') : data.display_name || '';
};

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(`${NOMINATIM_BASE}/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
    if (!res.ok) return '';
    const data = await res.json();
    return formatNominatimAddress(data);
  } catch {
    return '';
  }
};

const PlaceLocationFields = ({
  placeForm, setPlaceForm, groups, editingPlaceId,
  isSelectingLocation, onToggleSelectingLocation,
  isEditingShape, onToggleEditingShape, onPanToLocation,
}) => {
  const [gpsInput, setGpsInput] = useState('');

  const handleAddressSelect = useCallback((suggestion) => {
    const lat = Number(suggestion.lat);
    const lng = Number(suggestion.lon);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    setPlaceForm((prev) => ({ ...prev, coordinates: [lat, lng] }));
    onPanToLocation?.(lat, lng);
    reverseGeocode(lat, lng).then((addr) => {
      if (addr) setPlaceForm((prev) => ({ ...prev, address: addr }));
    });
  }, [onPanToLocation, setPlaceForm]);

  const handleGpsGo = useCallback(() => {
    const trimmed = gpsInput.trim();
    if (!trimmed) return;
    const parts = trimmed.split(/[,\s]+/).map(Number);
    if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return;
    const [lat, lng] = parts;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
    setPlaceForm((prev) => ({ ...prev, coordinates: [lat, lng] }));
    onPanToLocation?.(lat, lng);
    setGpsInput('');
    reverseGeocode(lat, lng).then((addr) => {
      if (addr) setPlaceForm((prev) => ({ ...prev, address: addr }));
    });
  }, [gpsInput, onPanToLocation, setPlaceForm]);

  return (
    <>
      <div className="space-y-1.5">
        <Label className="text-xs">Place Name *</Label>
        <Input
          value={placeForm.place_name}
          onChange={(e) => setPlaceForm((prev) => ({ ...prev, place_name: e.target.value }))}
          placeholder="Place name"
          className="h-8 text-sm"
          data-testid="place-name-input"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Address</Label>
        <AddressAutocomplete
          value={placeForm.address}
          onChange={(val) => setPlaceForm((prev) => ({ ...prev, address: val }))}
          onSelect={handleAddressSelect}
          placeholder="Search address..."
          inputClassName="h-8 text-base md:text-sm"
          data-testid="place-address-search"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">GPS Coordinates</Label>
        <div className="flex gap-1.5">
          <Input
            value={gpsInput}
            onChange={(e) => setGpsInput(e.target.value)}
            placeholder="lat, lng"
            className="h-8 text-base md:text-sm flex-1"
            onKeyDown={(e) => { if (e.key === 'Enter') handleGpsGo(); }}
            data-testid="place-gps-input"
          />
          <Button variant="outline" size="sm" className="h-8 px-2.5 shrink-0" onClick={handleGpsGo} data-testid="place-gps-go-btn" title="Go to coordinates">
            <Navigation className="w-3.5 h-3.5" />
          </Button>
        </div>
        {placeForm.coordinates?.length >= 2 ? (
          <p className="text-[10px] text-muted-foreground" data-testid="place-location-status">
            Current: {placeForm.coordinates[0].toFixed(6)}, {placeForm.coordinates[1].toFixed(6)}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Radius (meters)</Label>
          <Input
            type="number"
            value={placeForm.radius}
            onChange={(e) => setPlaceForm((prev) => ({ ...prev, radius: parseInt(e.target.value, 10) || 100 }))}
            className="h-8 text-sm"
            data-testid="place-radius-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Group</Label>
          <Select value={placeForm.place_group || '__none__'} onValueChange={(val) => setPlaceForm((prev) => ({ ...prev, place_group: val === '__none__' ? '' : val }))}>
            <SelectTrigger className="h-8 text-sm" data-testid="place-group-select">
              <SelectValue placeholder="No group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No group</SelectItem>
              {groups.filter((g) => g.group_type === 'place').map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <PlaceIconPicker value={placeForm.icon} onChange={(icon) => setPlaceForm((prev) => ({ ...prev, icon }))} />

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Switch checked={placeForm.show_name_on_map} onCheckedChange={(val) => setPlaceForm((prev) => ({ ...prev, show_name_on_map: val }))} data-testid="toggle-place-name-label" />
          <Label className="text-xs">Show name on map</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={placeForm.visible_on_map} onCheckedChange={(val) => setPlaceForm((prev) => ({ ...prev, visible_on_map: val }))} data-testid="toggle-place-visible" />
          <Label className="text-xs">Visible on map</Label>
        </div>
      </div>

      <div className="p-2.5 rounded-lg" data-testid="place-location-section">
        <Button variant={isSelectingLocation ? 'destructive' : 'outline'} size="sm" className="w-full h-8 text-xs dark:bg-[#172031]" data-testid="place-select-location-btn" onClick={onToggleSelectingLocation}>
          {isSelectingLocation ? 'Cancel Selection' : editingPlaceId ? 'Reselect Location' : 'Select on Map'}
        </Button>
        {editingPlaceId && placeForm.coordinates?.length >= 2 && !isSelectingLocation ? (
          <Button
            variant={isEditingShape ? 'default' : 'outline'}
            size="sm"
            className={`w-full h-8 text-xs mt-1.5 ${isEditingShape ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
            data-testid="place-edit-shape-btn"
            onClick={onToggleEditingShape}
          >
            <Pencil className="w-3 h-3 mr-1" />
            {isEditingShape ? 'Stop Editing Place' : 'Edit Place on Map'}
          </Button>
        ) : null}
      </div>
    </>
  );
};

export const GeofencingPanel = ({
  activePanel,
  groups,
  editingZoneId,
  editingPlaceId,
  editingRouteId,
  zoneForm,
  setZoneForm,
  placeForm,
  setPlaceForm,
  routeForm,
  setRouteForm,
  isDrawingZone,
  onToggleDrawingZone,
  isDrawingRoute,
  onToggleDrawingRoute,
  isSelectingLocation,
  onToggleSelectingLocation,
  isEditingShape,
  onToggleEditingShape,
  onPanToLocation,
  saving,
  onClose,
  onSave,
  hasSnapToRoad,
  isSnapping,
  onSnapToRoad,
  api,
  onRegionBoundaryLoaded,
}) => {
  const [regionQuery, setRegionQuery] = useState('');
  const [regionResults, setRegionResults] = useState([]);
  const [regionSearching, setRegionSearching] = useState(false);
  const [regionLoading, setRegionLoading] = useState(false);

  const handleRegionSearch = useCallback(async () => {
    if (!regionQuery.trim() || !api) return;
    setRegionSearching(true);
    setRegionResults([]);
    try {
      const res = await api.get(`/geofence-boundary-search?q=${encodeURIComponent(regionQuery.trim())}`);
      setRegionResults(res.data || []);
    } catch {
      setRegionResults([]);
    } finally {
      setRegionSearching(false);
    }
  }, [api, regionQuery]);

  const handleRegionSelect = useCallback(async (result) => {
    if (!api) return;
    setRegionLoading(true);
    try {
      const res = await api.post('/geofence-boundary-fetch', { osm_id: result.osm_id, display_name: result.display_name, source: result.source });
      const { coordinates, display_name } = res.data;
      if (coordinates?.length >= 3) {
        setZoneForm((prev) => ({
          ...prev,
          geometry: { type: 'region', coordinates },
          zone_name: prev.zone_name || display_name.split(',')[0].trim(),
        }));
        setRegionResults([]);
        setRegionQuery('');
        onRegionBoundaryLoaded?.(coordinates);
      }
    } catch {
      // Error handled by toast in parent
    } finally {
      setRegionLoading(false);
    }
  }, [api, setZoneForm, onRegionBoundaryLoaded]);
  if (!activePanel) return null;

  const panelTitle = activePanel === 'zone'
    ? editingZoneId ? 'Edit Zone' : 'Create Zone'
    : activePanel === 'place'
      ? editingPlaceId ? 'Edit Place' : 'Create Place'
      : editingRouteId ? 'Edit Route' : 'Create Route';

  const actionLabel = activePanel === 'zone'
    ? editingZoneId ? 'Update Zone' : 'Create Zone'
    : activePanel === 'place'
      ? editingPlaceId ? 'Update Place' : 'Create Place'
      : editingRouteId ? 'Update Route' : 'Create Route';

  return (
    <div className="h-full overflow-hidden rounded-xl border border-border bg-card shadow-sm flex flex-col" data-testid="geofence-panel">
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-2">
        <h3 className="text-sm font-bold" data-testid="geofence-panel-title">{panelTitle}</h3>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose} data-testid="close-panel-btn">
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      <ScrollArea className="flex-1 min-h-0" data-testid="geofence-panel-scroll-area">
        <div className="p-3 space-y-3">
          {activePanel === 'zone' ? (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Zone Name *</Label>
                <Input
                  value={zoneForm.zone_name}
                  onChange={(event) => setZoneForm((previous) => ({ ...previous, zone_name: event.target.value }))}
                  placeholder="Zone name"
                  className="h-8 text-sm"
                  data-testid="zone-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Select value={zoneForm.zone_type} onValueChange={(value) => setZoneForm((previous) => ({ ...previous, zone_type: value }))} disabled={!!editingZoneId}>
                  <SelectTrigger className="h-8 text-sm" data-testid="zone-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="polygon">Polygon</SelectItem>
                    <SelectItem value="circle">Circle</SelectItem>
                    <SelectItem value="rectangle">Rectangle</SelectItem>
                    <SelectItem value="region">State / Region</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ColorPickerField value={zoneForm.zone_color} onChange={(color) => setZoneForm((previous) => ({ ...previous, zone_color: color }))} />
              <div className="space-y-1.5">
                <Label className="text-xs">Group</Label>
                <Select value={zoneForm.zone_group || '__none__'} onValueChange={(value) => setZoneForm((previous) => ({ ...previous, zone_group: value === '__none__' ? '' : value }))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="zone-group-select">
                    <SelectValue placeholder="No group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No group</SelectItem>
                    {groups.filter((group) => group.group_type === 'zone').map((group) => (
                      <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {zoneForm.zone_type === 'circle' ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">Radius (meters)</Label>
                  <Input
                    type="number"
                    value={zoneForm.geometry?.radius || 500}
                    className="h-8 text-sm"
                    onChange={(event) => setZoneForm((previous) => ({
                      ...previous,
                      geometry: { ...previous.geometry, radius: parseInt(event.target.value, 10) || 500 },
                    }))}
                    data-testid="zone-radius-input"
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Switch checked={zoneForm.show_name_on_map} onCheckedChange={(value) => setZoneForm((previous) => ({ ...previous, show_name_on_map: value }))} data-testid="toggle-zone-name-label" />
                  <Label className="text-xs">Show name on map</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={zoneForm.visible_on_map} onCheckedChange={(value) => setZoneForm((previous) => ({ ...previous, visible_on_map: value }))} data-testid="toggle-zone-visible" />
                  <Label className="text-xs">Visible on map</Label>
                </div>
              </div>
              <div className="p-2.5 rounded-lg" data-testid="zone-shape-section">
                {zoneForm.zone_type === 'region' ? (
                  <>
                    {zoneForm.geometry?.coordinates?.length >= 3 ? (
                      <p className="text-xs text-muted-foreground mb-2" data-testid="zone-shape-status">
                        Region boundary loaded ({zoneForm.geometry.coordinates.length} points)
                      </p>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground mb-2" data-testid="zone-shape-status">
                          Search for a country or state/region
                        </p>
                        <div className="flex gap-1.5 mb-2">
                          <Input
                            value={regionQuery}
                            onChange={(e) => setRegionQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleRegionSearch(); }}
                            placeholder="e.g. New South Wales, Turkey..."
                            className="h-8 text-base md:text-sm flex-1"
                            data-testid="region-search-input"
                          />
                          <Button variant="outline" size="sm" className="h-8 px-2.5 shrink-0" onClick={handleRegionSearch} disabled={regionSearching || !regionQuery.trim()} data-testid="region-search-btn">
                            {regionSearching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                          </Button>
                        </div>
                        {regionResults.length > 0 ? (
                          <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="region-results">
                            {regionResults.map((r) => (
                              <button
                                key={r.osm_id}
                                type="button"
                                className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-background transition-colors flex items-center gap-1.5"
                                onClick={() => handleRegionSelect(r)}
                                disabled={regionLoading}
                                data-testid={`region-result-${r.osm_id}`}
                              >
                                <Globe className="w-3 h-3 shrink-0 text-[#2563eb]" />
                                <span className="truncate">{r.display_name}</span>
                              </button>
                            ))}
                          </div>
                        ) : null}
                        {regionSearching ? null : regionQuery && regionResults.length === 0 && !regionLoading ? null : null}
                        {regionLoading ? (
                          <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading boundary...
                          </div>
                        ) : null}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground mb-2" data-testid="zone-shape-status">
                      {(zoneForm.geometry?.coordinates?.length >= 3 || zoneForm.geometry?.center)
                        ? `Shape defined${zoneForm.geometry?.coordinates ? ` (${zoneForm.geometry.coordinates.length} points)` : ' (circle)'}`
                        : 'Draw the zone on the map'}
                    </p>
                    <Button variant={isDrawingZone ? 'destructive' : 'outline'} size="sm" className="w-full h-8 text-xs dark:bg-[#172031]" data-testid="zone-draw-btn" onClick={onToggleDrawingZone}>
                      {isDrawingZone ? 'Cancel Drawing' : editingZoneId ? 'Redraw Shape' : 'Start Drawing'}
                    </Button>
                    {editingZoneId && (zoneForm.geometry?.coordinates?.length >= 3 || zoneForm.geometry?.center) && !isDrawingZone ? (
                      <Button
                        variant={isEditingShape ? 'default' : 'outline'}
                        size="sm"
                        className={`w-full h-8 text-xs mt-1.5 ${isEditingShape ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                        data-testid="zone-edit-shape-btn"
                        onClick={onToggleEditingShape}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        {isEditingShape ? 'Stop Editing Shape' : 'Edit Shape on Map'}
                      </Button>
                    ) : null}
                  </>
                )}
              </div>
            </>
          ) : null}

          {activePanel === 'place' ? (
            <PlaceLocationFields
              placeForm={placeForm}
              setPlaceForm={setPlaceForm}
              groups={groups}
              editingPlaceId={editingPlaceId}
              isSelectingLocation={isSelectingLocation}
              onToggleSelectingLocation={onToggleSelectingLocation}
              isEditingShape={isEditingShape}
              onToggleEditingShape={onToggleEditingShape}
              onPanToLocation={onPanToLocation}
            />
          ) : null}

          {activePanel === 'route' ? (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Route Name *</Label>
                <Input
                  value={routeForm.route_name}
                  onChange={(event) => setRouteForm((previous) => ({ ...previous, route_name: event.target.value }))}
                  placeholder="Route name"
                  className="h-8 text-sm"
                  data-testid="route-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Deviation (m)</Label>
                <Input
                  type="number"
                  value={routeForm.deviation_distance}
                  onChange={(event) => setRouteForm((previous) => ({ ...previous, deviation_distance: parseInt(event.target.value, 10) || 100 }))}
                  className="h-8 text-sm"
                  data-testid="route-deviation-input"
                />
              </div>
              <ColorPickerField value={routeForm.route_color} onChange={(color) => setRouteForm((previous) => ({ ...previous, route_color: color }))} />
              <div className="space-y-1.5">
                <Label className="text-xs">Group</Label>
                <Select value={routeForm.route_group || '__none__'} onValueChange={(value) => setRouteForm((previous) => ({ ...previous, route_group: value === '__none__' ? '' : value }))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="route-group-select">
                    <SelectValue placeholder="No group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No group</SelectItem>
                    {groups.filter((group) => group.group_type === 'route').map((group) => (
                      <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Switch checked={routeForm.show_name_on_map} onCheckedChange={(value) => setRouteForm((previous) => ({ ...previous, show_name_on_map: value }))} data-testid="toggle-route-name-label" />
                  <Label className="text-xs">Show name on map</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={routeForm.visible_on_map} onCheckedChange={(value) => setRouteForm((previous) => ({ ...previous, visible_on_map: value }))} data-testid="toggle-route-visible" />
                  <Label className="text-xs">Visible on map</Label>
                </div>
              </div>
              <div className="p-2.5 rounded-lg" data-testid="route-shape-section">
                <p className="text-xs text-muted-foreground mb-2" data-testid="route-shape-status">
                  {routeForm.path_coordinates.length > 0 ? `${routeForm.path_coordinates.length} waypoint(s)` : 'Draw waypoints on the map'}
                </p>
                <Button variant={isDrawingRoute ? 'destructive' : 'outline'} size="sm" className="w-full h-8 text-xs dark:bg-[#172031]" data-testid="route-draw-btn" onClick={onToggleDrawingRoute}>
                  {isDrawingRoute ? 'Cancel Drawing' : editingRouteId ? 'Redraw Route' : 'Start Drawing'}
                </Button>
                {editingRouteId && routeForm.path_coordinates?.length >= 2 && !isDrawingRoute ? (
                  <Button
                    variant={isEditingShape ? 'default' : 'outline'}
                    size="sm"
                    className={`w-full h-8 text-xs mt-1.5 ${isEditingShape ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                    data-testid="route-edit-shape-btn"
                    onClick={onToggleEditingShape}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    {isEditingShape ? 'Stop Editing Path' : 'Edit Path on Map'}
                  </Button>
                ) : null}
                {hasSnapToRoad && routeForm.path_coordinates?.length >= 2 && !isDrawingRoute ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-8 text-xs mt-1.5"
                    data-testid="route-snap-to-road-btn"
                    onClick={onSnapToRoad}
                    disabled={isSnapping}
                  >
                    {isSnapping ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Navigation className="w-3 h-3 mr-1" />}
                    {isSnapping ? 'Snapping...' : 'Snap to Road'}
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </ScrollArea>

      <div className="border-t border-border bg-card p-3 flex gap-2">
        <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={onClose} data-testid="cancel-panel-btn">Cancel</Button>
        <Button
          size="sm"
          className="flex-1 h-8 text-xs bg-[#2563eb] hover:bg-[#1d4ed8]"
          disabled={saving}
          data-testid={activePanel === 'zone' ? 'save-zone-btn' : activePanel === 'place' ? 'save-place-btn' : 'save-route-btn'}
          onClick={onSave}
        >
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
          {actionLabel}
        </Button>
      </div>
    </div>
  );
};